import * as THREE from 'three';
import { Input } from '../../core/input/Input';
import { ChaseCamera } from '../../core/camera/ChaseCamera';
import { Car } from '../entities/Car';
import { CarController } from '../entities/CarController';
import { SimpleMap } from './SimpleMap';
import { resolveAABBXZ } from './collisions/AABBCollider';
import { KeyMap } from '../../core/input/KeyMap';
import { CAR_CATALOG, getCarByIndex, getCarIndexById } from '../cars/CarCatalog';
import { createCarFromStats } from '../cars/CarFactory';
import type { CarStats } from '../cars/CarTypes';
import { GhostPlayer } from '../race/GhostPlayer';
import { RaceManager } from '../race/RaceManager';
import type { GhostSample } from '../race/RaceTypes';
import { RoadRenderer } from '../../engine/RoadRenderer';
import { SkySystem } from '../../engine/SkySystem';
import { NeonEnvironmentRenderer } from '../../engine/NeonEnvironmentRenderer';
import { DriftBoostSystem, getDriftBoostHUDData } from '../../engine/DriftBoostSystem';
import { OpenWorldManager, type WeatherType, type PlayerStatus } from '../../engine/OpenWorldManager';
import { BW_CITIES } from '../../engine/OpenWorldMapLoader';
import { getUrbanTrackById, listUrbanTracks } from '../tracks/UrbanTrackCatalog';
import type { QualitySettings } from '../../core/quality/QualitySettings';
import { TrafficSystem } from '../../engine/TrafficSystem';

export class World {
  private car!: Car;
  private carController!: CarController;
  private readonly chase: ChaseCamera;
  private readonly map = new SimpleMap();
  private readonly sky: SkySystem;
  private selectedCarIndex = 0;
  private readonly tuningByCarId: Record<string, Partial<CarStats> & { colorHex?: string; neonColorHex?: string; neonIntensity?: number }> = {};
  private readonly race = new RaceManager();
  private readonly ghostPlayer = new GhostPlayer();
  private ghostEnabled = true;
  private ghostAvailable = false;
  private readonly ghostStorageKey = 'zgame_car_race_ghost_v1';
  private raceCompletedLatch = false;
  private roadRenderer!: RoadRenderer;
  private neonEnvironment: NeonEnvironmentRenderer | null = null;
  private driftBoostSystem: DriftBoostSystem;
  
  // Open World Mode - Baden-Württemberg
  private openWorldManager: OpenWorldManager | null = null;
  private openWorldMode = false;
  private openWorldPlayerStatus: PlayerStatus | null = null;
  private readonly weatherTypes: WeatherType[] = ['sunny', 'cloudy', 'rainy', 'foggy', 'snowy'];
  private currentWeatherIndex = 0;
  private currentCityIndex = 0;
  private readonly cityKeys = Object.keys(BW_CITIES) as Array<keyof typeof BW_CITIES>;
  private colliderDebugMode = false; // Track Collider Debug-Visualisierung
  
  // Urban Track Race State
  private raceSpawn = new THREE.Vector3(-10, 0.6, 40);
  private raceCheckpoints: import('../race/RaceTypes').Checkpoint[] = [];
  private activeTrackId: string | null = null;
  private raceDriveCorridor: Array<{ point: THREE.Vector3; halfWidth: number }> = [];
  private openWorldSpawnSnapped = false;
  
  private readonly quality: QualitySettings | null;
  private trafficSystem: TrafficSystem | null = null;

  constructor(
    private readonly scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    private readonly input: Input,
    opts?: { quality?: QualitySettings }
  ) {
    this.quality = opts?.quality ?? null;
    this.scene.add(this.map.group);
    this.sky = new SkySystem(this.scene);
    this.roadRenderer = new RoadRenderer(this.scene);
    this.roadRenderer.createTrack();
    this.chase = new ChaseCamera(camera);
    this.ghostPlayer.attach(this.scene);

    // NFS Underground Style Environment
    this.neonEnvironment = new NeonEnvironmentRenderer(this.scene);
    const urbanTracks = listUrbanTracks();
    if (urbanTracks.length > 0) {
      // Initial-Environment async laden (kein await im Constructor)
      this.neonEnvironment.renderEnvironment(urbanTracks[0]).catch(err => 
        console.error('Initial environment load failed:', err)
      );
    }
    
    // Drift & Boost System
    this.driftBoostSystem = new DriftBoostSystem();
    this.driftBoostSystem.attachToScene(this.scene);
    this.setupDriftBoostCallbacks();

    this.spawnCar(0);
  }

  update(dt: number) {
    this.handleCarSelection();
    this.handleRaceToggle();
    this.handleOpenWorldControls();
    const previousCarPosition = this.car.position.clone();
    this.carController.update(dt);
    if (this.input.anyPressed(KeyMap.reset)) {
      if (this.openWorldMode && this.openWorldManager) {
        const snap = this.openWorldManager.getMapLoader().getNearestRoadPoint(this.car.position, 220);
        if (snap) {
          this.car.position.copy(snap.point).setY(0.6);
          this.car.velocity.set(0, 0, 0);
          this.car.speed = 0;
          this.car.heading = 0;
          this.car.syncTransform();
        }
      } else {
        this.resetCar(true);
      }
    }
    
    // Open World Mode Update
    if (this.openWorldMode && this.openWorldManager) {
      this.trySnapOpenWorldSpawn();
      // Physische Collider-Barrieren prüfen (PRIORITY)
      const colliderManager = this.openWorldManager.getColliderManager();
      const collisionResult = colliderManager.checkCollision(this.car.position, 1.8);
      
      if (collisionResult && collisionResult.collision) {
        // Bounce-back Physik bei Kollision
        const bounceForce = collisionResult.normal.clone().multiplyScalar(collisionResult.penetrationDepth);
        this.carController.addPositionOffset(bounceForce.x, bounceForce.z);
        
        // Starke Geschwindigkeitsreduktion bei Kollision
        this.car.speed *= 0.05;
        
        // Optional: Bounce-Effekt in entgegengesetzter Richtung
        this.car.velocity.addScaledVector(collisionResult.normal, collisionResult.penetrationDepth * 5);
        
        console.log(`[Collision] Hit barrier! Speed reduced. Normal: (${collisionResult.normal.x.toFixed(2)}, ${collisionResult.normal.z.toFixed(2)})`);
      }
      
      // Soft Clamp als Backup (wenn keine harte Kollision)
      const snap = this.openWorldManager.getMapLoader().getNearestRoadPoint(this.car.position, 18);
      if (snap) {
        const offset = new THREE.Vector3().subVectors(this.car.position, snap.point);
        const roadWidth = snap.roadWidth ?? 6;
        const maxLateral = Math.max(1.8, roadWidth * 0.45);

        if (snap.direction) {
          const right = new THREE.Vector3(snap.direction.z, 0, -snap.direction.x).normalize();
          const lateral = offset.dot(right);
          const over = Math.abs(lateral) - maxLateral;
          if (over > 0) {
            const correction = right.multiplyScalar(over * Math.sign(lateral));
            this.carController.addPositionOffset(-correction.x, -correction.z);
            this.car.speed *= 0.15;
          } else if (offset.length() > 2.5) {
            const correction = Math.min(1, dt * 6);
            this.carController.addPositionOffset(-offset.x * correction, -offset.z * correction);
            this.car.speed *= 0.7;
          }
        } else {
          const distance = offset.length();
          const maxDistance = roadWidth * 0.5;
          if (distance > maxDistance) {
            const pushBack = offset.normalize().multiplyScalar(distance - maxDistance);
            this.carController.addPositionOffset(-pushBack.x, -pushBack.z);
            this.car.speed *= 0.25;
          } else if (distance > 2.5) {
            const correction = Math.min(1, dt * (distance > 8 ? 10 : 5));
            this.carController.addPositionOffset(-offset.x * correction, -offset.z * correction);
            this.car.speed *= distance > 8 ? 0.45 : 0.7;
          }
        }
      } else {
        this.car.speed *= 0.6;
      }
      const speedKmh = Math.abs(this.car.speed) * 3.6;
      this.openWorldPlayerStatus = this.openWorldManager.update(dt, this.car.position, speedKmh);
      this.trafficSystem?.update(dt, this.car.position);
    } else {
      if (this.neonEnvironment?.hasEnvironmentMap()) {
        const envHeight = this.neonEnvironment.getEnvironmentHeightAt(this.car.position.x, this.car.position.z);
        if (envHeight !== null) {
          this.car.position.y = Math.max(0.6, envHeight + 0.05);
        }
        const push = this.neonEnvironment.resolveEnvironmentCollision(previousCarPosition, this.car.position, 1.2);
        if (push) {
          this.carController.addPositionOffset(push.x, push.z);
          this.car.speed *= 0.5;
        }
      }
      this.enforceUrbanTrackBounds(dt);
      this.resolveCollisions();
      this.updateRace(dt);
    }
    
    this.updateDriftBoost(dt);
    this.chase.update(this.car.position, this.car.group.quaternion, dt);
    
    if (!this.openWorldMode) {
      this.sky.update(dt);
      this.neonEnvironment?.update(dt);
    }
  }
  
  private handleOpenWorldControls(): void {
    // Toggle Open World Mode (O)
    if (this.input.anyPressed(KeyMap.openWorld)) {
      this.toggleOpenWorldMode();
    }
    
    // Only handle these in Open World mode
    if (!this.openWorldMode || !this.openWorldManager) return;
    
    // Weather Cycle (Y)
    if (this.input.anyPressed(KeyMap.weatherCycle)) {
      this.currentWeatherIndex = (this.currentWeatherIndex + 1) % this.weatherTypes.length;
      this.openWorldManager.setWeather(this.weatherTypes[this.currentWeatherIndex], 0.6);
      console.log(`[OpenWorld] Wetter: ${this.weatherTypes[this.currentWeatherIndex]}`);
    }
    
    // Time Cycle (U)
    if (this.input.anyPressed(KeyMap.timeCycle)) {
      const currentTime = this.openWorldManager.getTimeOfDay();
      this.openWorldManager.setTimeOfDay((currentTime + 3) % 24);
      console.log(`[OpenWorld] Zeit: ${this.openWorldManager.getFormattedTime()}`);
    }
    
    // Teleport to next city (P)
    if (this.input.anyPressed(KeyMap.teleport)) {
      this.currentCityIndex = (this.currentCityIndex + 1) % this.cityKeys.length;
      const cityKey = this.cityKeys[this.currentCityIndex];
      const newPos = this.openWorldManager.getMapLoader().teleportToCity(cityKey);
      this.car.position.copy(newPos);
      this.car.velocity.set(0, 0, 0);
      this.car.speed = 0;
      this.car.syncTransform();
      console.log(`[OpenWorld] Teleport nach: ${BW_CITIES[cityKey].name}`);
    }
    
    // Toggle Collider Debug Visualisierung (V)
    if (this.input.anyPressed(KeyMap.debugColliders)) {
      this.colliderDebugMode = !this.colliderDebugMode;
      const colliderManager = this.openWorldManager.getColliderManager();
      colliderManager.setDebugMode(this.colliderDebugMode);
      const stats = colliderManager.getStats();
      console.log(`[Debug] Collider-Visualisierung: ${this.colliderDebugMode ? 'AN' : 'AUS'} (${stats.totalColliders} Colliders aktiv)`);
    }
  }
  
  toggleOpenWorldMode(): void {
    this.openWorldMode = !this.openWorldMode;
    
    if (this.openWorldMode) {
      // Aktiviere Open World
      console.log('[OpenWorld] 🌍 Open World Modus aktiviert - Baden-Württemberg');
      
      // Verstecke Race-Map Elemente
      this.map.group.visible = false;
      this.roadRenderer?.setVisible(false);
      this.neonEnvironment?.setVisible(false);
      
      // Erstelle Open World Manager
      if (!this.openWorldManager) {
        this.openWorldManager = new OpenWorldManager(this.scene);
        this.openWorldManager.onSpeedingDetected = (over, duration) => {
          console.log(`[OpenWorld] ⚠️ ${over.toFixed(0)} km/h zu schnell! (${duration.toFixed(1)}s)`);
        };
        this.openWorldManager.onCityEntered = (cityName) => {
          console.log(`[OpenWorld] 🏙️ Willkommen in ${cityName}!`);
        };
        this.openWorldManager.onWeatherChanged = (weather) => {
          console.log(`[OpenWorld] 🌤️ Wetter: ${weather.type}`);
        };
      }
      
      // Starte in Stuttgart
      const startPos = this.openWorldManager.initialize('stuttgart');
      this.car.position.copy(startPos);
      this.car.velocity.set(0, 0, 0);
      this.car.speed = 0;
      this.car.syncTransform();
      this.openWorldSpawnSnapped = false;
      
      // Traffic (instanced cars + simple occupants)
      if (!this.trafficSystem) {
        const count = this.quality?.world.trafficCount ?? 12;
        this.trafficSystem = new TrafficSystem(
          this.scene,
          count,
          (pos) => this.openWorldManager?.getMapLoader().getNearestRoadPoint(pos, 14) ?? null
        );
      }
      this.trafficSystem.resetAround(this.car.position);
      
    } else {
      // Deaktiviere Open World
      console.log('[OpenWorld] 🏁 Zurück zum Rennmodus');
      
      // Zeige Race-Map Elemente
      this.map.group.visible = true;
      this.roadRenderer?.setVisible(true);
      this.neonEnvironment?.setVisible(true);
      
      // Dispose Traffic
      if (this.trafficSystem) {
        this.trafficSystem.dispose();
        this.trafficSystem = null;
      }
      
      // Dispose Open World (optional - kann beibehalten werden für schnelleres Umschalten)
      if (this.openWorldManager) {
        this.openWorldManager.dispose();
        this.openWorldManager = null;
      }
      
      // Reset Car Position
      this.resetCar(true);
    }
  }

  setOpenWorldMode(next: boolean): void {
    if (this.openWorldMode === next) return;
    this.toggleOpenWorldMode();
  }

  setCarById(carId: string): void {
    const nextIndex = getCarIndexById(carId);
    if (nextIndex === this.selectedCarIndex) return;
    this.selectedCarIndex = nextIndex;
    this.spawnCar(this.selectedCarIndex, true);
  }

  setUrbanTrack(trackId: string): void {
    const track = getUrbanTrackById(trackId);
    if (!track || !this.neonEnvironment) return;

    this.activeTrackId = trackId;
    this.race.setLaps(track.raceType === 'circuit' ? (track.laps || 1) : 1);

    // Render NFS environment + road (async)
    this.neonEnvironment.renderEnvironment(track).catch(err => 
      console.error('Track environment load failed:', err)
    );

    // Race spawn + checkpoints from track
    this.raceSpawn.copy(track.spawnPoint).setY(0.6);

    this.raceCheckpoints = track.checkpoints.map((cp) => ({
      id: cp.id,
      position: cp.position.clone(),
      radius: cp.radius
    }));
    if (track.environmentMapPath) {
      this.raceDriveCorridor = [];
    } else {
      this.buildRaceDriveCorridor(track);
    }
    this.resetCar(true);
  }

  private buildRaceDriveCorridor(track: NonNullable<ReturnType<typeof getUrbanTrackById>>): void {
    this.raceDriveCorridor = [];

    let position = track.spawnPoint.clone();
    let yaw = 0;

    for (const seg of track.segments) {
      const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      const segmentSamples = Math.max(6, Math.floor(seg.length / 12));

      for (let sample = 0; sample <= segmentSamples; sample += 1) {
        const distance = (seg.length * sample) / segmentSamples;
        const samplePoint = position.clone().addScaledVector(dir, distance);
        this.raceDriveCorridor.push({
          point: samplePoint,
          halfWidth: Math.max(5, seg.width * 0.65)
        });
      }

      position.addScaledVector(dir, seg.length);

      if (seg.type === 'left_curve') yaw -= (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'right_curve') yaw += (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'hairpin') yaw += (Math.PI * 0.85) * (Math.random() < 0.5 ? 1 : -1);
      if (seg.type === 'chicane') yaw += (Math.random() < 0.5 ? 1 : -1) * 0.55;
    }
  }

  private enforceUrbanTrackBounds(dt: number): void {
    if (this.openWorldMode || !this.activeTrackId || this.raceDriveCorridor.length === 0) return;

    let nearest = this.raceDriveCorridor[0];
    let nearestDistSq = this.car.position.distanceToSquared(nearest.point);

    for (let i = 1; i < this.raceDriveCorridor.length; i += 1) {
      const candidate = this.raceDriveCorridor[i];
      const d2 = this.car.position.distanceToSquared(candidate.point);
      if (d2 < nearestDistSq) {
        nearest = candidate;
        nearestDistSq = d2;
      }
    }

    const nearestDist = Math.sqrt(nearestDistSq);
    const maxAllowed = nearest.halfWidth;
    if (nearestDist <= maxAllowed) return;

    const over = nearestDist - maxAllowed;
    const direction = new THREE.Vector3().subVectors(this.car.position, nearest.point).setY(0);
    if (direction.lengthSq() < 1e-6) return;
    direction.normalize();

    const correctionStrength = Math.min(1, dt * 10);
    const correction = direction.multiplyScalar(over * correctionStrength);
    this.carController.addPositionOffset(-correction.x, -correction.z);

    const speedDrop = over > 8 ? 0.5 : 0.75;
    this.car.speed *= speedDrop;
  }

  private setupDriftBoostCallbacks(): void {
    this.driftBoostSystem.onDriftStarted(() => {
      console.log('[World] 🚗 Drift started!');
    });
    
    this.driftBoostSystem.onDriftEnded((score) => {
      console.log(`[World] 🎯 Drift ended! Score: ${score}`);
    });
    
    this.driftBoostSystem.onNitroStarted(() => {
      console.log('[World] 🔥 NOS activated!');
    });
    
    this.driftBoostSystem.onDriftComboReached((multiplier, score) => {
      console.log(`[World] ⚡ Drift Combo x${multiplier.toFixed(1)} - Score: ${Math.round(score)}`);
    });
  }

  private updateDriftBoost(dt: number): void {
    const handbrake = this.input.anyDown(KeyMap.handbrake);
    const nitroPressed = this.input.anyDown(KeyMap.nitro);
    
    // Calculate velocity direction from car velocity
    const velDir = Math.atan2(this.car.velocity.x, this.car.velocity.z);
    
    const { speedBonus } = this.driftBoostSystem.update(
      dt,
      this.car.position,
      this.car.heading,
      velDir,
      Math.abs(this.car.speed) * 3.6, // Convert to km/h
      handbrake,
      nitroPressed
    );
    
    // Apply speed bonus from NOS
    if (speedBonus > 0) {
      const boostAccel = speedBonus * 0.5 * dt;
      this.car.speed += this.car.speed >= 0 ? boostAccel : -boostAccel;
    }
  }

  private handleCarSelection() {
    const previous = this.selectedCarIndex;

    if (this.input.anyPressed(KeyMap.car1)) this.selectedCarIndex = 0;
    if (this.input.anyPressed(KeyMap.car2)) this.selectedCarIndex = 1;
    if (this.input.anyPressed(KeyMap.car3)) this.selectedCarIndex = 2;
    if (this.input.anyPressed(KeyMap.car4)) this.selectedCarIndex = 3;
    if (this.input.anyPressed(KeyMap.car5)) this.selectedCarIndex = 4;
    if (this.input.anyPressed(KeyMap.car6)) this.selectedCarIndex = 5;
    if (this.input.anyPressed(KeyMap.car7)) this.selectedCarIndex = 6;

    if (this.selectedCarIndex !== previous) {
      this.spawnCar(this.selectedCarIndex, true);
    }
  }

  private handleRaceToggle() {
    if (this.input.anyPressed(KeyMap.raceStart)) {
      this.startRace();
    }
  }

  private updateRace(dt: number) {
    this.race.update(dt);
    this.race.checkCheckpoint(this.raceCheckpoints, this.car.position);
    this.race.recordGhost(this.car.position, this.car.heading);

    const state = this.race.getState();
    if (this.ghostEnabled && state.active) {
      this.ghostPlayer.update(state.time);
    }

    if (state.completed && !this.raceCompletedLatch) {
      this.saveGhostIfBest(state.bestTime);
      this.raceCompletedLatch = true;
    }
    if (!state.completed) {
      this.raceCompletedLatch = false;
    }
  }

  private trySnapOpenWorldSpawn(): void {
    if (this.openWorldSpawnSnapped || !this.openWorldManager) return;
    const snap = this.openWorldManager.getMapLoader().getNearestRoadPoint(this.car.position, 220);
    if (!snap) return;
    this.car.position.copy(snap.point).setY(0.6);
    this.car.velocity.set(0, 0, 0);
    this.car.speed = 0;
    this.car.syncTransform();
    this.openWorldSpawnSnapped = true;
  }

  startRace() {
    this.resetCar(true);
    this.race.start();
    const samples = this.loadGhost();
    this.ghostAvailable = samples.length > 1;
    if (this.ghostAvailable && this.ghostEnabled) {
      this.ghostPlayer.setSamples(samples);
    }
  }

  resetCar(toStart = false) {
    if (toStart) {
      this.car.position.copy(this.raceSpawn);
      this.car.heading = 0;
      this.car.velocity.set(0, 0, 0);
      this.car.speed = 0;
      this.car.syncTransform();
      return;
    }
    this.car.position.set(0, 0.6, 0);
    this.car.speed = 0;
    this.car.velocity.set(0, 0, 0);
    this.car.heading = 0;
    this.car.syncTransform();
  }

  setGhostEnabled(next: boolean) {
    this.ghostEnabled = next;
    if (!next) {
      this.ghostPlayer.detach(this.scene);
    } else {
      this.ghostPlayer.attach(this.scene);
      const samples = this.loadGhost();
      this.ghostAvailable = samples.length > 1;
      this.ghostPlayer.setSamples(samples);
    }
  }

  setCarTuning(carId: string, tuning: Partial<CarStats> & { colorHex?: string; neonColorHex?: string; neonIntensity?: number }) {
    this.tuningByCarId[carId] = { ...tuning };
    if (this.car?.stats.id === carId) {
      this.spawnCar(this.selectedCarIndex, true);
    }
  }

  private spawnCar(index: number, keepPosition = false) {
    const chosen = getCarByIndex(index);
    const tuned = this.tuningByCarId[chosen.id] || {};
    const merged: CarStats = { ...chosen, ...tuned };

    const prevPos = this.car?.position.clone();
    const prevHeading = this.car?.heading ?? 0;

    if (this.car) {
      this.scene.remove(this.car.group);
    }

    this.car = createCarFromStats(merged, tuned);
    this.scene.add(this.car.group);

    if (keepPosition && prevPos) {
      this.car.position.copy(prevPos);
      this.car.heading = prevHeading;
      this.car.syncTransform();
    }

    this.carController = new CarController(this.car, this.input);
  }

  private resolveCollisions() {
    const halfW = 1.1;
    const halfD = 2.2;
    const p = this.car.position;

    const moving = {
      min: new THREE.Vector3(p.x - halfW, 0, p.z - halfD),
      max: new THREE.Vector3(p.x + halfW, 2, p.z + halfD)
    };

    for (const collider of this.map.colliders) {
      const push = resolveAABBXZ(moving, collider);
      if (!push) continue;

      this.carController.addPositionOffset(push.x, push.z);
      moving.min.x += push.x;
      moving.max.x += push.x;
      moving.min.z += push.z;
      moving.max.z += push.z;
    }
  }

  getDebugText(fps: number): string {
    const car = this.car;
    const stats = car.stats;
    const speedKmh = Math.abs(car.speed) * 3.6;
    const maxKmh = stats.maxSpeed * 3.6;
    const list = CAR_CATALOG
      .map((item, index) => `${index + 1}. ${item.name}${index === this.selectedCarIndex ? '  <' : ''}`)
      .join('\n');

    // Open World Mode spezifische Infos
    if (this.openWorldMode && this.openWorldManager && this.openWorldPlayerStatus) {
      const ps = this.openWorldPlayerStatus;
      const weather = this.openWorldManager.getWeather();
      return [
        `FPS: ${fps}`,
        `Mode: 🌍 OPEN WORLD - Baden-Württemberg`,
        `Car: ${stats.name}`,
        '',
        `📍 GPS: ${ps.gpsLocation.lat.toFixed(4)}°N, ${ps.gpsLocation.lon.toFixed(4)}°E`,
        `🏙️ ${ps.gpsLocation.cityName || 'Ländliches Gebiet'}`,
        `🛤️ ${ps.currentRoadType || 'Offroad'} | Limit: ${ps.speedLimit || '-'} km/h`,
        `⚡ Speed: ${speedKmh.toFixed(0)} / ${maxKmh.toFixed(0)} km/h`,
        `${ps.isOverspeed ? '⚠️ GESCHWINDIGKEITSÜBERSCHREITUNG!' : ''}`,
        '',
        `🌤️ Wetter: ${weather.type} | 🕐 ${this.openWorldManager.getFormattedTime()} Uhr`,
        '',
        'Controls (Open World):',
        'O  - Zurück zum Rennmodus',
        'Y  - Wetter wechseln',
        'U  - Tageszeit (+3h)',
        'P  - Teleport zur nächsten Stadt',
        'Shift/N - NOS Boost',
        'Space - Drift'
      ].join('\n');
    }

    const raceState = this.race.getState();
    const raceLine = raceState.active
      ? `Race: ${raceState.time.toFixed(2)}s (CP ${raceState.checkpointIndex + 1}/${this.map.checkpoints.length})`
      : `Race: idle${raceState.bestTime ? ` | Best ${raceState.bestTime.toFixed(2)}s` : ''}`;

    return [
      `FPS: ${fps}`,
      `Car: ${stats.name} [${stats.id}]`,
      raceLine,
      `Speed: ${speedKmh.toFixed(0)} / ${maxKmh.toFixed(0)} km/h`,
      `Pos: ${car.position.x.toFixed(1)}, ${car.position.z.toFixed(1)}`,
      '',
      'Stats:',
      `Accel: ${stats.accel.toFixed(1)}  Brake: ${stats.brake.toFixed(1)}  Handling: ${stats.handling.toFixed(2)}`,
      `Weight: ${stats.weight}kg  Grip: ${stats.grip.toFixed(1)}  DriftGrip: ${stats.driftGrip.toFixed(1)}  Drag: ${stats.drag.toFixed(2)}`,
      '',
      'Select car: 1..5',
      `${list}`,
      '',
      'Controls:',
      'W/S or ↑/↓  throttle/brake',
      'A/D or ←/→  steer',
      'Space        handbrake (drift)',
      'R            reset',
      'T            start race',
      'O            🌍 Open World (Baden-Württemberg)'
    ].join('\n');
  }

  getUiState() {
    const state = this.race.getState();
    const cpIdx = state.checkpointIndex;
    const cpTarget = cpIdx < this.map.checkpoints.length ? this.map.checkpoints[cpIdx]?.position || null : null;

    // Open World spezifische Daten
    const openWorldData = this.openWorldMode && this.openWorldManager && this.openWorldPlayerStatus ? {
      isOpenWorld: true,
      playerStatus: this.openWorldPlayerStatus,
      weather: this.openWorldManager.getWeather(),
      timeOfDay: this.openWorldManager.getFormattedTime(),
      availableCities: this.cityKeys.map(key => ({
        key,
        name: BW_CITIES[key].name
      }))
    } : {
      isOpenWorld: false,
      playerStatus: null,
      weather: null,
      timeOfDay: null,
      availableCities: []
    };

    return {
      carId: this.car.stats.id,
      carName: this.car.stats.name,
      position: this.car.position.clone(),
      heading: this.car.heading,
      raceActive: state.active,
      raceTime: state.time,
      raceCompleted: state.completed,
      playerPlace: (state as any).playerPlace ?? null,
      bestTime: state.bestTime,
      checkpointIndex: cpIdx,
      splits: state.splits,
      ghostAvailable: this.ghostAvailable,
      ghostEnabled: this.ghostEnabled,
      checkpointCount: this.map.checkpoints.length,
      checkpointTarget: cpTarget as THREE.Vector3 | null,
      laps: state.laps,
      currentLap: state.currentLap,
      mapBounds: this.map.bounds,
      // Drift & Boost Data
      driftBoost: getDriftBoostHUDData(this.driftBoostSystem),
      driftScore: getDriftBoostHUDData(this.driftBoostSystem).driftScore ?? 0,
      speed: Math.abs(this.car.speed) * 3.6,  // km/h
      maxSpeed: this.car.stats.maxSpeed * 3.6,
      districtTier: 1 as 1 | 2 | 3,
      heatLevel: this.openWorldMode && this.openWorldPlayerStatus
        ? Math.min(3, Math.floor((this.openWorldPlayerStatus.wantedLevel || 0) / 2))
        : 0,
      // Open World Mode
      openWorld: openWorldData
    };
  }

  private saveGhostIfBest(bestTime: number | null) {
    const samples = this.race.getGhost();
    if (samples.length < 5) return;

    try {
      const existingBest = this.loadGhostBestTime();
      if (existingBest !== null && bestTime !== null && bestTime >= existingBest) return;
      localStorage.setItem(this.ghostStorageKey, JSON.stringify({ bestTime, samples }));
      this.ghostAvailable = true;
    } catch {
      // ignore
    }
  }

  private loadGhostBestTime(): number | null {
    try {
      const raw = localStorage.getItem(this.ghostStorageKey);
      if (!raw) return null;
      const data = JSON.parse(raw) as { bestTime?: number };
      return typeof data.bestTime === 'number' ? data.bestTime : null;
    } catch {
      return null;
    }
  }

  private loadGhost(): GhostSample[] {
    try {
      const raw = localStorage.getItem(this.ghostStorageKey);
      if (!raw) return [];
      const data = JSON.parse(raw) as { samples?: GhostSample[] };
      return Array.isArray(data.samples) ? data.samples : [];
    } catch {
      return [];
    }
  }
}
